import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Link,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import html2canvas from 'html2canvas';
import { useSelector } from 'react-redux';
import { QRCode } from 'react-qrcode-logo';
import peacemakerImg from '../../../assets/images/homeimages/peacemakerImg.png';
import { Configuration } from '../../../assets/data/config';
import { jsPDF } from 'jspdf';
import imgUrl from '../../../assets/images/imgUrl';
import {apiServices} from '../../../services/apiServices';

const VisitingCard = () => {
  const { user } = useSelector((state) => state.auth);
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [currentPage, setCurrentPage] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [certificateImage, setCertificateImage] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState(null);
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  
  // New state for visiting card from API
  const [visitingCardImage, setVisitingCardImage] = useState(null);
  const [visitingCardBlob, setVisitingCardBlob] = useState(null); // Store the original blob data
  const [visitingCardLoading, setVisitingCardLoading] = useState(false);
  const [visitingCardError, setVisitingCardError] = useState(null);
  const [isVisitingCardAutoGenerating, setIsVisitingCardAutoGenerating] = useState(false);
  const [refetchAttempts, setRefetchAttempts] = useState(0);
  
  const visitingCardRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to check if a blob URL is still valid
  const isValidBlobUrl = (url) => {
    if (!url || !url.startsWith('blob:')) return false;
    try {
      // Try to create a new URL object to validate the blob URL
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // User data for API calls
  const userData = {
    name: user?.username || user?.name || 'User Name',
    country: user?.country || 'Country',
    idNumber: user?.peacekeeper_id || 'IN-0000000-PK',
    mobile: user?.phone || user?.mobile || 'Mobile Number',
    email: user?.email || 'email@example.com',
    photo: user?.profile_picture || user?.avatar || peacemakerImg
  };


  // Fetch visiting card from API
  const fetchVisitingCard = async (forceRefetch = false) => {
    if (user?.userid) {
      // Don't refetch if we already have a valid visiting card image (unless forced)
      if (!forceRefetch && visitingCardImage && isValidBlobUrl(visitingCardImage)) {
        console.log('Visiting card image already exists and is valid, skipping fetch');
        return;
      }
      
      setVisitingCardLoading(true);
      setVisitingCardError(null);
      try {
        console.log('Fetching visiting card for user:', user.userid);
        // Use getBlobAPI to get the visiting card image as blob
        const response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
        
        if (response.data) {
          console.log('Visiting card API response received, data size:', response.data.size || response.data.byteLength);
          
          // Create blob URL from the response
          const blob = new Blob([response.data], { type: 'image/png' });
          console.log('Blob created, size:', blob.size, 'type:', blob.type);
          
          // Store the original blob data for downloads
          setVisitingCardBlob(blob);
          
          const imageUrl = URL.createObjectURL(blob);
          console.log('Blob URL created:', imageUrl);
          
          // Test the blob URL immediately
          const testImg = new Image();
          testImg.onload = () => {
            console.log('Blob URL test successful, setting visiting card image');
            setVisitingCardImage(imageUrl);
            setRefetchAttempts(0); // Reset refetch attempts on successful fetch
            setVisitingCardLoading(false);
          };
          testImg.onerror = (error) => {
            console.error('Blob URL test failed:', error);
            setVisitingCardError('Failed to load visiting card image');
            setVisitingCardLoading(false);
            // Clean up the invalid blob URL
            URL.revokeObjectURL(imageUrl);
          };
          testImg.src = imageUrl;
        } else {
          console.log('No data received from visiting card API');
          setVisitingCardError('No visiting card data received');
          setVisitingCardLoading(false);
        }
      } catch (error) {
        console.error('Error fetching visiting card:', error);
        
        // Check if it's a 404 error (visiting card not found)
        if (error.response?.status === 404) {
          console.log('Visiting card not found (404), attempting to generate automatically...');
          console.log('Error details:', error.response);
          setDownloadStatus('Visiting card not found. Generating automatically...');
          
          // Automatically call the generate API
          try {
            setIsVisitingCardAutoGenerating(true);
            const updateResponse = await apiServices.putAPI(`update-visiting-card/${userData.idNumber}`, {
              forceRegenerate: true
            });
            
            console.log('Visiting card generation response:', updateResponse);
            
            if (updateResponse.status === 200) {
              setDownloadStatus('Visiting card generated successfully! Fetching...');
              
              // Wait a moment for the visiting card to be generated, then retry fetching
              setTimeout(async () => {
                try {
                  const retryResponse = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
                  
                  if (retryResponse.data) {
                    const blob = new Blob([retryResponse.data], { type: 'image/png' });
                    setVisitingCardBlob(blob); // Store the blob data
                    const imageUrl = URL.createObjectURL(blob);
                    setVisitingCardImage(imageUrl);
                    setDownloadStatus('Visiting card generated and loaded successfully!');
                    console.log('Visiting card fetched after generation:', imageUrl);
                  }
                } catch (retryError) {
                  console.error('Error fetching visiting card after generation:', retryError);
                  setVisitingCardError('Visiting card was generated but failed to load. Please refresh the page.');
                } finally {
                  setIsVisitingCardAutoGenerating(false);
                }
              }, 2000); // Wait 2 seconds for generation to complete
              
            } else {
              throw new Error('Failed to generate visiting card');
            }
          } catch (updateError) {
            console.error('Error generating visiting card:', updateError);
            setVisitingCardError('Failed to generate visiting card. Please try again later.');
            setIsVisitingCardAutoGenerating(false);
          }
        } else {
          // It's a different error, show the original error message
          setVisitingCardError('Failed to load visiting card. Please try again later.');
        }
      } finally {
        setVisitingCardLoading(false);
      }
    }
  };

  // Manual visiting card generation function
  const handleGenerateVisitingCard = async () => {
    try {
      setIsGenerating(true);
      setDownloadStatus('Generating new visiting card...');

      // Call the PUT endpoint to generate/update the visiting card
      const updateResponse = await apiServices.putAPI(`update-visiting-card/${userData.idNumber}`, {
        forceRegenerate: true
      });

      console.log('Visiting card generation response:', updateResponse);

      if (updateResponse.status === 200) {
        setDownloadStatus('Visiting card generated successfully! Fetching...');

        // Wait a moment for the visiting card to be generated, then fetch it
        setTimeout(async () => {
          try {
            const retryResponse = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);

            if (retryResponse.data) {
              const blob = new Blob([retryResponse.data], { type: 'image/png' });
              setVisitingCardBlob(blob); // Store the blob data
              const imageUrl = URL.createObjectURL(blob);
              setVisitingCardImage(imageUrl);
              setDownloadStatus('Visiting card generated and loaded successfully!');
              console.log('Visiting card fetched after generation:', imageUrl);
            }
          } catch (retryError) {
            console.error('Error fetching visiting card after generation:', retryError);
            setDownloadStatus('Visiting card was generated but failed to load. Please refresh the page.');
          }
        }, 2000); // Wait 2 seconds for generation to complete

      } else {
        throw new Error('Failed to generate visiting card');
      }
    } catch (error) {
      console.error('Error generating visiting card:', error);
      setDownloadStatus('Failed to generate visiting card. Please try again later.');
    } finally {
      setIsGenerating(false);
    }
  };


  // Fetch visiting card when component mounts
  useEffect(() => {
    fetchVisitingCard();
  }, [user?.userid]);


  // Fetch certificate image
  useEffect(() => {
    const fetchCertificate = async () => {
      if (user?.userid) {
        setCertificateLoading(true);
        setCertificateError(null);
        try {
          // Use getBlobAPI to get the certificate image as blob
          const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);
          
          if (response.data) {
            // Create blob URL from the response
            const blob = new Blob([response.data], { type: 'image/png' });
            const imageUrl = URL.createObjectURL(blob);
            setCertificateImage(imageUrl);
            console.log('Certificate fetched successfully:', imageUrl);
          }
        } catch (error) {
          console.error('Error fetching certificate:', error);
          
          // Check if it's a 404 error (certificate not found)
          if (error.response?.status === 404) {
            console.log('Certificate not found (404), attempting to generate automatically...');
            console.log('Error details:', error.response);
            setShareStatus('Certificate not found. Generating automatically...');
            
            // Automatically call the generate API
            try {
              setIsAutoGenerating(true);
              const updateResponse = await apiServices.putAPI(`update-certificate/${userData.idNumber}`, {
                forceRegenerate: true
              });
              
              console.log('Certificate generation response:', updateResponse);
              
              if (updateResponse.status === 200) {
                setShareStatus('Certificate generated successfully! Fetching...');
                
                // Wait a moment for the certificate to be generated, then retry fetching
                setTimeout(async () => {
                  try {
                    const retryResponse = await apiServices.getBlobAPI(`certificate/${user.userid}`);
                    
                    if (retryResponse.data) {
                      const blob = new Blob([retryResponse.data], { type: 'image/png' });
                      const imageUrl = URL.createObjectURL(blob);
                      setCertificateImage(imageUrl);
                      setShareStatus('Certificate generated and loaded successfully!');
                      console.log('Certificate fetched after generation:', imageUrl);
                    }
                  } catch (retryError) {
                    console.error('Error fetching certificate after generation:', retryError);
                    setCertificateError('Certificate was generated but failed to load. Please refresh the page.');
                  } finally {
                    setIsAutoGenerating(false);
                  }
                }, 2000); // Wait 2 seconds for generation to complete
                
              } else {
                throw new Error('Failed to generate certificate');
              }
            } catch (updateError) {
              console.error('Error generating certificate:', updateError);
              setCertificateError('Failed to generate certificate. Please try again later.');
              setIsAutoGenerating(false);
            }
          } else {
            // It's a different error, show the original error message
            setCertificateError('Failed to load certificate. Please try again later.');
          }
        } finally {
          setCertificateLoading(false);
        }
      }
    };

    fetchCertificate();
  }, [user?.userid]);

  // Listen for certificate updates from EditProfile.js
  useEffect(() => {
    const handleCertificateUpdate = async (event) => {
      console.log('Certificate update event received in VisitingCard:', event.detail);
      
      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing certificate image in VisitingCard after profile update...');
        setDownloadStatus('Certificate updated! Refreshing with new profile information...');
        
        // Wait a moment for the backend to process, then refresh
        setTimeout(async () => {
          try {
            const response = await apiServices.getBlobAPI(`certificate/${user.userid}`);
            
            if (response.data) {
              // Clean up old blob URL to prevent memory leaks
              if (certificateImage && certificateImage.startsWith('blob:')) {
                URL.revokeObjectURL(certificateImage);
              }
              
              // Create new blob URL from the response
              const blob = new Blob([response.data], { type: 'image/png' });
              const imageUrl = URL.createObjectURL(blob);
              setCertificateImage(imageUrl);
              setDownloadStatus('Certificate refreshed successfully with updated profile information!');
              console.log('Certificate refreshed in VisitingCard after profile update:', imageUrl);
              
              // Clear success message after 3 seconds
              setTimeout(() => {
                setDownloadStatus('');
              }, 3000);
            }
          } catch (error) {
            console.error('Error refreshing certificate in VisitingCard after profile update:', error);
            setDownloadStatus('Certificate refresh failed. Please refresh the page manually.');
          }
        }, 2000); // Wait 2 seconds for backend processing
      }
    };

    // Add event listener
    window.addEventListener('certificateUpdated', handleCertificateUpdate);

    // Cleanup event listener
    return () => {
      window.removeEventListener('certificateUpdated', handleCertificateUpdate);
    };
  }, [user?.userid, certificateImage]);

  // Listen for visiting card updates from EditProfile.js
  useEffect(() => {
    const handleVisitingCardUpdate = async (event) => {
      console.log('Visiting card update event received in VisitingCard:', event.detail);
      
      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing visiting card image in VisitingCard after profile update...');
        setDownloadStatus('Visiting card updated! Refreshing with new profile information...');
        
        // Wait a moment for the backend to process, then refresh
        setTimeout(async () => {
          try {
            const response = await apiServices.getBlobAPI(`visiting-card/${user.userid}`);
            
            if (response.data) {
              // Clean up old blob URL to prevent memory leaks
              if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
                URL.revokeObjectURL(visitingCardImage);
              }
              
              // Create new blob URL from the response
              const blob = new Blob([response.data], { type: 'image/png' });
              setVisitingCardBlob(blob); // Store the blob data
              const imageUrl = URL.createObjectURL(blob);
              setVisitingCardImage(imageUrl);
              setDownloadStatus('Visiting card refreshed successfully with updated profile information!');
              console.log('Visiting card refreshed in VisitingCard after profile update:', imageUrl);
              
              // Clear success message after 3 seconds
              setTimeout(() => {
                setDownloadStatus('');
              }, 3000);
            }
          } catch (error) {
            console.error('Error refreshing visiting card in VisitingCard after profile update:', error);
            setDownloadStatus('Visiting card refresh failed. Please refresh the page manually.');
          }
        }, 2000); // Wait 2 seconds for backend processing
      }
    };

    // Add event listener
    window.addEventListener('visitingCardUpdated', handleVisitingCardUpdate);

    // Cleanup event listener
    return () => {
      window.removeEventListener('visitingCardUpdated', handleVisitingCardUpdate);
    };
  }, [user?.userid]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
        URL.revokeObjectURL(visitingCardImage);
      }
    };
  }, [visitingCardImage]);

  // Generate QR code data - registration URL based on usertype and temp_peacekeeper_id
  const generateQRCodeData = () => {
    const domain = Configuration.frontendDomain;
    const peacekeeperId = user?.peacekeeper_id || 'IN-0000000-PK';

    // Priority 1: For Entity usertype with categoryname
    if (user?.usertype === 'Entity' && user?.categoryname) {
      const cleanName = (user.name || user.username || 'User')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      const cleanCategory = user.categoryname
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');
      return `${domain}/register?${cleanName}-${cleanCategory}-${peacekeeperId}`;
    }

    // Priority 2: For users referred by entities (have temp_peacekeeper_id)
    if (user?.temp_peacekeeper_id) {
      return `${domain}/register?${user.temp_peacekeeper_id}`;
    }

    // Priority 3: For regular users
    return `${domain}/register?referencenumber=${peacekeeperId}`;
  };

  const qrCodeData = generateQRCodeData();

  const buildFilename = () => {
    // Extract name parts for proper formatting
    const safeName = (userData.name || 'User')
      .toString()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters but keep spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
    
    // Get peacekeeper ID and clean it
    const peacekeeperId = (userData.idNumber || 'IN-00000000-PK').toString().trim();
    
    // Format: VC-FirstName-LastName-PeacekeeperID-IAPM
    const filename = `VC-${safeName}-${peacekeeperId}`;
    
    return filename;
  };

  // Helper function to check if download attribute is supported
  const isDownloadSupported = () => {
    const link = document.createElement('a');
    return 'download' in link;
  };

  // Helper function to detect iOS devices
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Helper function to detect Safari browser
  const isSafari = () => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  };

  // iOS-specific download handler
  const handleIOSDownload = (blob, filename) => {
    try {
      console.log('Handling iOS download for:', filename);
      setDownloadStatus('Preparing download for iOS...');

      // Determine file type for proper MIME type
      const isPDF = filename.toLowerCase().endsWith('.pdf');
      const mimeType = isPDF ? 'application/pdf' : 'image/png';

      // For iOS, we need to use a different approach
      if (navigator.share && navigator.canShare) {
        // Try Web Share API first (iOS 13+)
        try {
          const file = new File([blob], filename, { type: mimeType });
          
          if (navigator.canShare({ files: [file] })) {
            const shareTitle = isPDF ? 'I Am Peacekeeper Visiting Card PDF' : 'I Am Peacekeeper Visiting Card';
            const shareText = isPDF ? `Download your visiting card PDF: ${filename}` : `Download your visiting card: ${filename}`;
            
            navigator.share({
              files: [file],
              title: shareTitle,
              text: shareText
            }).then(() => {
              setDownloadStatus('File shared successfully! Check your chosen app.');
            }).catch((error) => {
              console.log('Web Share failed, trying alternative method:', error);
              handleIOSFallbackDownload(blob, filename, isPDF);
            });
            return;
          }
        } catch (shareError) {
          console.log('Web Share API error, using fallback:', shareError);
          handleIOSFallbackDownload(blob, filename, isPDF);
          return;
        }
      }

      // Fallback for older iOS versions or if Web Share fails
      handleIOSFallbackDownload(blob, filename, isPDF);

    } catch (error) {
      console.error('iOS download failed:', error);
      handleIOSFallbackDownload(blob, filename, filename.toLowerCase().endsWith('.pdf'));
    }
  };

  // iOS-specific PDF download handler - Force save to device
  const handleIOSPDFDownload = (blob, filename) => {
    try {
      console.log('Handling iOS PDF download for:', filename);
      setDownloadStatus('Preparing PDF download for iOS...');

      // For iOS PDFs, we need to force save to device, not open in browser
      // Try multiple approaches in sequence
      
      // Approach 1: Try Web Share API first (most reliable for iOS)
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], filename, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) {
            navigator.share({
              files: [file],
              title: 'I Am Peacekeeper Visiting Card PDF',
              text: `Save your visiting card PDF: ${filename}`
            }).then(() => {
              setDownloadStatus('PDF shared successfully! Choose "Save to Files" to save to your device.');
            }).catch((shareError) => {
              console.log('Web Share failed, trying alternative method:', shareError);
              handleIOSPDFFallback(blob, filename);
            });
            return;
          }
        } catch (shareError) {
          console.log('Web Share API error, trying fallback:', shareError);
          handleIOSPDFFallback(blob, filename);
          return;
        }
      }

      // If Web Share is not available, use fallback
      handleIOSPDFFallback(blob, filename);

    } catch (error) {
      console.error('iOS PDF download failed:', error);
      handleIOSPDFFallback(blob, filename);
    }
  };

  // iOS PDF fallback method - More aggressive approach
  const handleIOSPDFFallback = (blob, filename) => {
    try {
      console.log('Using iOS PDF fallback method for:', filename);
      setDownloadStatus('Preparing PDF for device save...');

      // Create a new blob with explicit PDF MIME type
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      
      // Try to use Cordova file system if available
      if (window.cordova && window.cordova.file) {
        console.log('Using Cordova file system for PDF save...');
        setDownloadStatus('Saving PDF to device using Cordova...');
        
        saveFileToDevice(pdfBlob, filename).then((filePath) => {
          console.log('PDF saved successfully to:', filePath);
          setDownloadStatus(`PDF saved to device: ${filename}`);
          
          // Show toast notification if available
          if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
            window.cordova.plugins.toast.show(`PDF saved: ${filename}`, 'long', 'bottom');
          }
        }).catch((error) => {
          console.error('Cordova save failed, trying web fallback:', error);
          handleIOSPDFWebFallback(pdfBlob, filename);
        });
        return;
      }

      // Web fallback for non-Cordova environments
      handleIOSPDFWebFallback(pdfBlob, filename);

    } catch (error) {
      console.error('iOS PDF fallback failed:', error);
      setDownloadStatus('PDF save failed. Please try the Web Share option.');
    }
  };

  // iOS PDF web fallback - Force download without opening
  const handleIOSPDFWebFallback = (pdfBlob, filename) => {
    try {
      console.log('Using iOS PDF web fallback for:', filename);
      setDownloadStatus('Preparing PDF download...');

      // Create a data URL instead of blob URL to force download
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        
        // Create a temporary link element with aggressive download settings
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add attributes to force download
        link.setAttribute('download', filename);
        link.setAttribute('type', 'application/pdf');
        link.setAttribute('rel', 'noopener');
        
        // Add to DOM
        document.body.appendChild(link);
        
        // Try multiple click approaches
        try {
          // Method 1: Direct click
          link.click();
          
          // Method 2: Synthetic click event
          setTimeout(() => {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            link.dispatchEvent(clickEvent);
          }, 50);
          
          // Method 3: Programmatic click as last resort
          setTimeout(() => {
            if (link.click) {
              link.click();
            }
          }, 100);
          
          // Method 4: Try iframe approach for iOS (more aggressive)
          setTimeout(() => {
            try {
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = dataUrl;
              document.body.appendChild(iframe);
              
              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                }
              }, 1000);
            } catch (iframeError) {
              console.log('Iframe method failed:', iframeError);
            }
          }, 200);
          
        } catch (clickError) {
          console.log('Click methods failed:', clickError);
        }
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 2000);

        setDownloadStatus('PDF download initiated. Check your Downloads or Files app.');
        
        // Show instructions for manual saving if needed
        setTimeout(() => {
          setDownloadStatus('If PDF opened in browser, long press the PDF and select "Save to Files" or "Add to Files".');
        }, 2000);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading PDF blob:', error);
        setDownloadStatus('Failed to prepare PDF for download. Please try again.');
      };
      
      reader.readAsDataURL(pdfBlob);

    } catch (error) {
      console.error('iOS PDF web fallback failed:', error);
      setDownloadStatus('PDF download failed. Please try the Web Share option above.');
    }
  };

  // iOS fallback download method
  const handleIOSFallbackDownload = (blob, filename, isPDF = false) => {
    try {
      console.log('Using iOS fallback download method for:', filename, 'isPDF:', isPDF);
      setDownloadStatus('Opening file for iOS download...');

      // For PDFs on iOS, we need a different approach to prevent opening in the app
      if (isPDF) {
        // Create a blob URL instead of data URL for PDFs to avoid opening in browser
        const blobUrl = URL.createObjectURL(blob);
        
        // Create a temporary link element with proper attributes
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM and trigger download
        document.body.appendChild(link);
        
        // For iOS PDFs, we need to trigger the click in a user gesture
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        
        link.dispatchEvent(clickEvent);
        
        // Clean up blob URL after a delay
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(blobUrl);
        }, 2000);

        setDownloadStatus('PDF download initiated. Check your Files app or Downloads.');
        
        // Show specific instructions for PDF downloads on iOS
        setTimeout(() => {
          setDownloadStatus('If PDF didn\'t download, try the "Download All Pages as PDF" button again or check your Files app.');
        }, 3000);

        return;
      }

      // For images, use the original data URL approach
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        
        // Create a temporary link element
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM and trigger download
        document.body.appendChild(link);
        
        // For iOS, we need to trigger the click in a user gesture
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true
        });
        
        link.dispatchEvent(clickEvent);
        
        // Clean up
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 1000);

        setDownloadStatus('Download initiated. Check your Downloads folder.');
        
        // Show instructions for iOS users
        setTimeout(() => {
          setDownloadStatus('If download didn\'t start, long press the image above and select "Save to Photos" or "Add to Files"');
        }, 2000);

        // Additional fallback: try opening in new tab for iOS
        setTimeout(() => {
          const newWindow = window.open(dataUrl, '_blank');
          if (newWindow) {
            setDownloadStatus('File opened in new tab. Long press and save on iOS.');
          }
        }, 3000);
      };
      
      reader.onerror = (error) => {
        console.error('Error reading blob for iOS:', error);
        setDownloadStatus('Failed to prepare file for download. Please try again.');
      };
      
      reader.readAsDataURL(blob);

    } catch (error) {
      console.error('iOS fallback download failed:', error);
      setDownloadStatus('Download failed. Please try saving the file manually.');
    }
  };

  // Create mobile download dialog with better user experience
  const createMobileDownloadDialog = (blob, filename) => {
    try {
      console.log('Creating mobile download dialog for:', filename);
      setDownloadStatus('Creating download dialog...');
      
      const url = URL.createObjectURL(blob);
      
      // Create a more prominent download dialog
      const downloadContainer = document.createElement('div');
      downloadContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.8);
        z-index: 10000;
        display: flex;
        justify-content: center;
        align-items: center;
      `;

      const dialogBox = document.createElement('div');
      dialogBox.style.cssText = `
        background: white;
        padding: 30px;
        border-radius: 15px;
        text-align: center;
        max-width: 90%;
        box-shadow: 0 10px 30px rgba(0,0,0,0.5);
      `;

      const title = document.createElement('h3');
      title.textContent = 'Download File';
      title.style.cssText = 'margin: 0 0 20px 0; color: #333; font-size: 20px;';

      const instructionText = document.createElement('p');
      instructionText.textContent = `Tap the button below to download: ${filename}`;
      instructionText.style.cssText = 'margin: 0 0 20px 0; color: #666; font-size: 16px;';

      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = filename;
      downloadLink.textContent = `Download ${filename}`;
      downloadLink.style.cssText = `
        display: block;
        padding: 15px 30px;
        background: #007bff;
        color: white;
        text-decoration: none;
        border-radius: 8px;
        font-size: 18px;
        font-weight: bold;
        margin: 10px 0;
      `;

      const closeButton = document.createElement('button');
      closeButton.textContent = 'Close';
      closeButton.style.cssText = `
        padding: 10px 20px;
        background: #6c757d;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        margin-top: 10px;
        font-size: 16px;
      `;

      closeButton.onclick = () => {
        document.body.removeChild(downloadContainer);
        URL.revokeObjectURL(url);
      };

      dialogBox.appendChild(title);
      dialogBox.appendChild(instructionText);
      dialogBox.appendChild(downloadLink);
      dialogBox.appendChild(closeButton);
      downloadContainer.appendChild(dialogBox);
      document.body.appendChild(downloadContainer);

      setDownloadStatus('Download dialog opened. Tap the download button above.');

      // Auto-remove after 60 seconds
      setTimeout(() => {
        if (document.body.contains(downloadContainer)) {
          document.body.removeChild(downloadContainer);
          URL.revokeObjectURL(url);
        }
      }, 60000);

    } catch (error) {
      console.error('Mobile download dialog failed:', error);
      fallbackDownload(blob, filename);
    }
  };

  // Alternative Android download method - similar to certificate download
  const tryAndroidDownload = (blob, filename) => {
    try {
      console.log('Trying alternative Android download method...');
      setDownloadStatus('Trying alternative Android download...');
      
      // Use the same approach as certificate download - create blob URL and download
      const url = URL.createObjectURL(blob);
      
      // Create a download link similar to certificate download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
      
      setDownloadStatus(`File downloaded: ${filename}`);
      
      // Show toast notification if available
      if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
        window.cordova.plugins.toast.show(`Downloaded: ${filename}`, 'long', 'bottom');
      }
      
    } catch (error) {
      console.error('Android download method failed:', error);
      setDownloadStatus('Android download failed, using fallback...');
      fallbackDownload(blob, filename);
    }
  };

  // Helper function to save file to a specific directory
  const saveFileToDirectory = (dirEntry, blob, filename, resolve, reject) => {
    dirEntry.getFile(filename, { create: true, exclusive: false }, (fileEntry) => {
      fileEntry.createWriter((fileWriter) => {
        fileWriter.onwriteend = () => {
          const filePath = fileEntry.nativeURL || fileEntry.fullPath;
          console.log('File saved successfully to:', filePath);
          setDownloadStatus(`File saved to device: ${filename}`);
          resolve(filePath);
        };
        fileWriter.onerror = (error) => {
          console.error('Error writing file:', error);
          reject(error);
        };
        fileWriter.write(blob);
      }, (error) => {
        console.error('Error creating file writer:', error);
        reject(error);
      });
    }, (error) => {
      console.error('Error creating file:', error);
      reject(error);
    });
  };

  // Save file to device using Cordova
  const saveFileToDevice = async (blob, filename) => {
    return new Promise((resolve, reject) => {
      try {
        // Check if we're in a Cordova environment
        if (window.cordova && window.cordova.file) {
          console.log('Using Cordova file API for device save');

          // Get the appropriate directory (prefer user-accessible locations)
          let directory;
          try {
            // Try to use user-accessible directories first
            if (window.cordova.file.externalRootDirectory) {
              // Try Downloads folder first
              directory = window.cordova.file.externalRootDirectory + 'Download/';
            } else if (window.cordova.file.documentsDirectory) {
              // Documents folder is usually accessible
              directory = window.cordova.file.documentsDirectory;
            } else if (window.cordova.file.externalDataDirectory) {
              // External data directory
              directory = window.cordova.file.externalDataDirectory;
            } else if (window.cordova.file.cacheDirectory) {
              // Cache directory as fallback
              directory = window.cordova.file.cacheDirectory;
            } else {
              // Last resort
              directory = window.cordova.file.dataDirectory;
            }
          } catch (dirError) {
            console.log('Error getting directory, using fallback:', dirError);
            directory = window.cordova.file.dataDirectory;
          }

          console.log('Using directory:', directory);

          // Use the modern Cordova file API
          if (window.resolveLocalFileSystemURL) {
            window.resolveLocalFileSystemURL(directory, (dirEntry) => {
              dirEntry.getFile(filename, { create: true, exclusive: false }, (fileEntry) => {
                fileEntry.createWriter((fileWriter) => {
                  fileWriter.onwriteend = () => {
                    const filePath = fileEntry.nativeURL || fileEntry.fullPath;
                    console.log('File saved successfully to:', filePath);
                    
                    // Provide more helpful success message
                    let successMessage = `File saved: ${filename}`;
                    if (directory.includes('Download')) {
                      successMessage += '\nCheck your Downloads folder';
                    } else if (directory.includes('Documents')) {
                      successMessage += '\nCheck your Documents folder';
                    } else {
                      successMessage += '\nFile saved to app storage';
                    }
                    
                    setDownloadStatus(successMessage);
                    
                    // Show toast notification if available
                    if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
                      window.cordova.plugins.toast.show(`Downloaded: ${filename}`, 'long', 'bottom');
                    }
                    
                    resolve(filePath);
                  };

                  fileWriter.onerror = (error) => {
                    console.error('Error writing file:', error);
                    reject(error);
                  };

                  // Write the blob directly
                  fileWriter.write(blob);
                }, (error) => {
                  console.error('Error creating file writer:', error);
                  reject(error);
                });
              }, (error) => {
                console.error('Error creating file:', error);
                // Try to create the directory first if it doesn't exist
                if (error.code === 1) { // NOT_FOUND_ERR = 1
                  console.log('Directory not found, trying to create it...');
                  
                  // Try multiple directory creation strategies
                  const tryCreateDirectory = (baseDir, dirName, callback) => {
                    window.resolveLocalFileSystemURL(baseDir, (rootEntry) => {
                      rootEntry.getDirectory(dirName, { create: true }, (dirEntry) => {
                        callback(null, dirEntry);
                      }, (dirError) => {
                        console.log(`Failed to create ${dirName} in ${baseDir}, trying alternative...`);
                        callback(dirError, null);
                      });
                    }, (baseError) => {
                      console.log(`Failed to access ${baseDir}, trying alternative...`);
                      callback(baseError, null);
                    });
                  };
                  
                  // Try Downloads directory first
                  tryCreateDirectory(window.cordova.file.externalRootDirectory, 'Download', (err, dirEntry) => {
                    if (err) {
                      // Try Documents directory
                      tryCreateDirectory(window.cordova.file.documentsDirectory, '', (err2, dirEntry2) => {
                        if (err2) {
                          // Try external data directory
                          tryCreateDirectory(window.cordova.file.externalDataDirectory, '', (err3, dirEntry3) => {
                            if (err3) {
                              console.error('All directory creation attempts failed');
                              reject(err3);
                            } else {
                              saveFileToDirectory(dirEntry3, blob, filename, resolve, reject);
                            }
                          });
                        } else {
                          saveFileToDirectory(dirEntry2, blob, filename, resolve, reject);
                        }
                      });
                    } else {
                      saveFileToDirectory(dirEntry, blob, filename, resolve, reject);
                    }
                  });
                } else {
                  reject(error);
                }
              });
            }, (err) => {
              console.error('Error accessing directory:', err);
              reject(err);
            });
          } else {
            // Fallback to legacy method if modern API not available
            console.log('Using legacy Cordova file API');
            
            // Convert blob to base64 for legacy API
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const base64Data = reader.result.split(',')[1];
                
                window.cordova.file.writeFile(directory, filename, base64Data, true,
                  (success) => {
                    const filePath = directory + filename;
                    console.log('File saved successfully to:', filePath);
                    setDownloadStatus(`File saved to device: ${filename}`);
                    resolve(filePath);
                  },
                  (error) => {
                    console.error('Error writing file with legacy API:', error);
                    reject(error);
                  }
                );
              } catch (conversionError) {
                console.error('Error converting blob to base64:', conversionError);
                reject(conversionError);
              }
            };
            
            reader.onerror = (error) => {
              console.error('Error reading blob:', error);
              reject(error);
            };
            
            reader.readAsDataURL(blob);
          }
        } else {
          // Not in Cordova environment - use web download
          console.log('Not in Cordova environment, using web download');
          try {
            // Create a download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';

            // Add to DOM, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL after a delay
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 1000);

            // Return a mock file path for consistency
            const mockPath = `file:///downloads/${filename}`;
            setDownloadStatus(`File downloaded: ${filename}`);
            resolve(mockPath);
          } catch (fallbackError) {
            console.error('Web download fallback failed:', fallbackError);
            reject(fallbackError);
          }
        }
      } catch (error) {
        console.error('Error in saveFileToDevice:', error);
        reject(error);
      }
    });
  };

  const fallbackDownload = (blob, filename) => {
    try {
      console.log('Using fallback download for:', filename);

      // Ensure the blob is valid
      if (!blob || blob.size === 0) {
        console.error('Invalid blob for download:', blob);
        setDownloadStatus('Download failed: Invalid file data');
        return;
      }

      // Check if we're on a mobile device
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        console.log('Mobile device detected, using mobile-friendly download...');
        setDownloadStatus('Opening file for mobile download...');

        // For mobile, try multiple approaches
        const url = URL.createObjectURL(blob);
        
        // First try: Direct download link
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        // Add to DOM, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
        
        setDownloadStatus(`Download initiated: ${filename}`);
        
        // If that doesn't work, try opening in new tab
        setTimeout(() => {
          const newWindow = window.open(url, '_blank');
          if (newWindow) {
            setDownloadStatus('File opened in new tab. Long press and save on mobile.');
            setTimeout(() => {
              URL.revokeObjectURL(url);
            }, 5000);
          }
        }, 1000);
        
        return;
      }

      // Desktop download logic
      console.log('Desktop device, using standard download...');

      // Check if download attribute is supported
      if (isDownloadSupported()) {
        // Create a download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(link);

        // Trigger download
        link.click();

        // Clean up
        document.body.removeChild(link);

        // Clean up the URL after a delay
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);

        console.log('Fallback download completed for:', filename);
        setDownloadStatus(`Visiting card downloaded: ${filename}`);
      } else {
        // Download attribute not supported, open in new tab
        console.log('Download attribute not supported, opening in new tab...');
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setDownloadStatus('Visiting card opened in new tab. Right-click and save as...');
      }
      
    } catch (error) {
      console.error('Error in fallbackDownload:', error);
      setDownloadStatus('Download failed. Please try again.');

      // Try one more time with a different approach
      try {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setDownloadStatus('Visiting card opened in new tab');
      } catch (finalError) {
        console.error('Final download attempt failed:', finalError);
        setDownloadStatus('Download completely failed. Please check your browser settings.');
      }
    }
  };

  const downloadBlob = (blob, filename) => {
    try {
      console.log('Starting download for:', filename, 'Blob size:', blob.size);

      // Enhanced mobile download handling with Cordova support
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isIOSDevice = isIOS();
      const isSafariBrowser = isSafari();
      
      console.log('Device type - Mobile:', isMobile, 'iOS:', isIOSDevice, 'Safari:', isSafariBrowser, 'Cordova available:', !!window.cordova);

      // Handle iOS devices specifically
      if (isIOSDevice) {
        console.log('iOS device detected, using iOS-specific download handler');
        
        // Use specific PDF handler for PDF files on iOS
        if (filename.toLowerCase().endsWith('.pdf')) {
          console.log('iOS PDF download detected, using specialized PDF handler');
          handleIOSPDFDownload(blob, filename);
        } else {
          handleIOSDownload(blob, filename);
        }
        return;
      }

      // Try Cordova file handling first if available
      if (window.cordova && window.cordova.file) {
        console.log('Attempting Cordova file save...');
        setDownloadStatus('Saving to device...');

        saveFileToDevice(blob, filename).then((filePath) => {
          console.log('File saved successfully to:', filePath);
          setDownloadStatus(`File saved to device: ${filename}`);
          
          // For Android, try to show a notification or toast
          if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
            window.cordova.plugins.toast.show(`File saved: ${filename}`, 'long', 'bottom');
          }
        }).catch((error) => {
          console.error('Cordova save failed, trying alternative methods:', error);
          
          // Try Web Share API first for mobile
          if (isMobile && navigator.share && navigator.canShare) {
            console.log('Trying Web Share API as fallback...');
            const fileType = filename.endsWith('.pdf') ? 'application/pdf' : 'image/png';
            const file = new File([blob], filename, { type: fileType });
            
            if (navigator.canShare({ files: [file] })) {
              navigator.share({
                files: [file],
                title: filename.endsWith('.pdf') ? 'Peacekeeper Visiting Card PDF' : 'Peacekeeper Visiting Card',
                text: `Download your ${filename.endsWith('.pdf') ? 'visiting card PDF' : 'visiting card image'}`
              }).then(() => {
                setDownloadStatus('File shared successfully! Check your chosen app.');
              }).catch((shareError) => {
                console.log('Web Share failed, trying download dialog:', shareError);
                createMobileDownloadDialog(blob, filename);
              });
              return;
            }
          }
          
          // Try alternative Android download method
          if (isMobile && /Android/i.test(navigator.userAgent)) {
            tryAndroidDownload(blob, filename);
          } else {
            setDownloadStatus('Device save failed, using download...');
            fallbackDownload(blob, filename);
          }
        });
        return;
      }

      // Try Web Share API for mobile devices (for images and PDFs)
      if (isMobile && navigator.share && navigator.canShare) {
        console.log('Attempting Web Share API...');
        const fileType = filename.endsWith('.pdf') ? 'application/pdf' : 'image/png';
        const file = new File([blob], filename, { type: fileType });
        
        if (navigator.canShare({ files: [file] })) {
          const shareTitle = filename.endsWith('.pdf') ? 'Peacekeeper Visiting Card PDF' : 'Peacekeeper Visiting Card';
          navigator.share({
            files: [file],
            title: shareTitle,
            text: `Download your ${filename.endsWith('.pdf') ? 'visiting card PDF' : 'visiting card image'}`
          }).then(() => {
            setDownloadStatus('File shared successfully!');
          }).catch((shareError) => {
            console.log('Web Share failed, falling back to download:', shareError);
            setDownloadStatus('Sharing failed, downloading instead...');
            fallbackDownload(blob, filename);
          });
          return;
        }
      }

      // For mobile devices, create a more user-friendly download experience
      if (isMobile) {
        console.log('Mobile device detected, creating mobile-friendly download...');
        console.log('Blob details - size:', blob.size, 'type:', blob.type, 'filename:', filename);
        setDownloadStatus('Preparing mobile download...');

        // Use the improved mobile download dialog
        createMobileDownloadDialog(blob, filename);
        return;
      }

      // Regular download for desktop or when other methods are not available
      console.log('Using fallback download method...');
      setDownloadStatus('Downloading file...');
      fallbackDownload(blob, filename);

    } catch (error) {
      console.error('Error:', error);
      setDownloadStatus('Download failed. Please try again.');
      // Ultimate fallback
      fallbackDownload(blob, filename);
    }
  };

  const handleDownloadPNG = async () => {
    try {
      setIsGenerating(true);
      setDownloadStatus('Preparing download...');

      let imageUrl;
      let filename;

      // Determine which image to download based on current page
      if (currentPage === 0) {
        // Main visiting card - check if blob data is available
        if (!visitingCardBlob) {
          setDownloadStatus('Visiting card image not loaded yet. Please wait...');
          // Try to fetch the visiting card if not available
          try {
            await fetchVisitingCard();
            // Wait a moment for the image to load
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (!visitingCardBlob) {
              throw new Error('Visiting card image is still not available');
            }
          } catch (error) {
            console.error('Error fetching visiting card:', error);
            throw new Error('Failed to load visiting card image. Please try again.');
          }
        }
        
        // Use the stored blob data directly for visiting card
        const blob = visitingCardBlob;
        filename = buildFilename();
        
        // Ensure filename has .png extension
        if (!filename.toLowerCase().endsWith('.png')) {
          filename = filename + '.png';
        }
        
        console.log('Using stored visiting card blob for download, size:', blob.size, 'type:', blob.type, 'filename:', filename);
        
        // Use the simplified approach that was working before
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isIOSDevice = isIOS();

        // Handle iOS devices with specific logic
        if (isIOSDevice) {
          console.log('iOS device detected in PNG download, using iOS handler');
          handleIOSDownload(blob, filename);
          return;
        }

        if (window.cordova) {
          // For mobile devices, try to force download
          try {
            // Use the stored blob data directly instead of fetching
            console.log("Using stored blob for download:", filename, "Blob size:", blob.size);
            
            // Try multiple Android directories for better accessibility
            const trySaveToDirectory = (directory, dirName) => {
              return new Promise((resolve, reject) => {
                window.resolveLocalFileSystemURL(directory, (dirEntry) => {
                  dirEntry.getFile(filename, { create: true }, (fileEntry) => {
                    fileEntry.createWriter((fileWriter) => {
                      fileWriter.onwriteend = () => {
                        const filePath = fileEntry.nativeURL || fileEntry.fullPath;
                        console.log(`File saved successfully to ${dirName}:`, filePath);
                        console.log(`Filename used: ${filename}`);
                        console.log(`File extension: ${filename.split('.').pop()}`);
                        setDownloadStatus(`Visiting card saved to ${dirName}: ${filename}`);
                        
                        // Show toast notification
                        if (window.cordova && window.cordova.plugins && window.cordova.plugins.toast) {
                          window.cordova.plugins.toast.show(`Saved to ${dirName}: ${filename}`, 'long', 'bottom');
                        }
                        
                        resolve(filePath);
                      };
                      
                      fileWriter.onerror = (error) => {
                        console.error(`Error writing to ${dirName}:`, error);
                        reject(error);
                      };
                      
                      // Ensure the blob has correct MIME type for PNG
                      const pngBlob = new Blob([blob], { type: 'image/png' });
                      fileWriter.write(pngBlob);
                    }, (error) => {
                      console.error(`Error creating writer for ${dirName}:`, error);
                      reject(error);
                    });
                  }, (error) => {
                    console.error(`Error creating file in ${dirName}:`, error);
                    reject(error);
                  });
                }, (error) => {
                  console.error(`Error accessing ${dirName}:`, error);
                  reject(error);
                });
              });
            };

            // Try different directories in order of preference
            const directories = [
              { path: window.cordova.file.externalRootDirectory + "Download/", name: "Downloads" },
              { path: window.cordova.file.externalDataDirectory, name: "External Data" },
              { path: window.cordova.file.documentsDirectory, name: "Documents" },
              { path: window.cordova.file.cacheDirectory, name: "Cache" }
            ];

            // Try each directory until one succeeds
            const tryNextDirectory = async (index) => {
              if (index >= directories.length) {
                throw new Error('All directory save attempts failed');
              }
              
              try {
                await trySaveToDirectory(directories[index].path, directories[index].name);
              } catch (error) {
                console.log(`Failed to save to ${directories[index].name}, trying next...`);
                await tryNextDirectory(index + 1);
              }
            };

            tryNextDirectory(0).catch((error) => {
              console.error('All save attempts failed:', error);
              setDownloadStatus('Failed to save to device. Trying alternative method...');
              
              // Fallback to the alternative method
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              link.textContent = 'Click to Download Visiting Card';
              link.style.cssText = 'display: block; padding: 15px; margin: 15px; background: #007bff; color: white; text-decoration: none; border-radius: 8px; text-align: center; font-size: 16px; font-weight: bold;';

              document.body.appendChild(link);

              // Remove after 30 seconds
              setTimeout(() => {
                if (document.body.contains(link)) {
                  document.body.removeChild(link);
                }
                URL.revokeObjectURL(url);
              }, 30000);

              setDownloadStatus('Download link created above. Click the blue button to download.');
            });

          } catch (mobileError) {
            console.error('Mobile download failed, trying alternative method:', mobileError);

            // Alternative: try to create a clickable download link
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.textContent = 'Click to Download Visiting Card';
            link.style.cssText = 'display: block; padding: 15px; margin: 15px; background: #007bff; color: white; text-decoration: none; border-radius: 8px; text-align: center; font-size: 16px; font-weight: bold;';

            document.body.appendChild(link);

            // Remove after 30 seconds
            setTimeout(() => {
              if (document.body.contains(link)) {
                document.body.removeChild(link);
              }
              URL.revokeObjectURL(url);
            }, 30000);

            setDownloadStatus('Download link created above. Click the blue button to download.');
          }
        } else {
          // For desktop, use standard download
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up URL
          setTimeout(() => URL.revokeObjectURL(url), 1000);

          setDownloadStatus(`Visiting card downloaded: ${filename}`);
        }
        
        return; // Exit early since we have the blob data
      } else if (currentPage === 1) {
        // Static page 1
        imageUrl = imgUrl.visitingCard2;
        const baseFilename = buildFilename().replace('.png', '');
        filename = `${baseFilename}-Page2.png`;
      } else if (currentPage === 2) {
        // Static page 2
        imageUrl = imgUrl.visitingCard3;
        const baseFilename = buildFilename().replace('.png', '');
        filename = `${baseFilename}-Page3.png`;
      } else if (currentPage === 3) {
        // Static page 3
        imageUrl = imgUrl.visitingCard4;
        const baseFilename = buildFilename().replace('.png', '');
        filename = `${baseFilename}-Page4.png`;
      } else {
        throw new Error('No image available for current page');
      }

      // Validate image URL before fetching
      if (!imageUrl) {
        throw new Error('Image URL is not available');
      }

      console.log('Fetching image from URL:', imageUrl);
      
      // For blob URLs, check if they're still valid
      if (imageUrl.startsWith('blob:')) {
        try {
          // Test if the blob URL is still valid
          const testResponse = await fetch(imageUrl);
          if (!testResponse.ok) {
            throw new Error('Blob URL is no longer valid');
          }
        } catch (error) {
          console.error('Blob URL validation failed:', error);
          if (currentPage === 0) {
            // For visiting card, try to refetch
            setDownloadStatus('Refreshing visiting card image...');
            await fetchVisitingCard(true);
            await new Promise(resolve => setTimeout(resolve, 1000));
            if (visitingCardImage) {
              imageUrl = visitingCardImage;
            } else {
              throw new Error('Failed to refresh visiting card image');
            }
          } else {
            throw new Error('Image URL is no longer valid');
          }
        }
      }
      
      // Convert the image to blob for proper handling
      let blob;
      
      if (imageUrl.startsWith('blob:')) {
        // For blob URLs, fetch directly
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        blob = await response.blob();
      } else {
        // For static images, fetch and convert to blob
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        blob = await response.blob();
      }

      // Ensure blob is valid
      if (!blob || blob.size === 0) {
        throw new Error('Invalid image data received');
      }

      // Ensure correct MIME type for PNG
      if (!blob.type || blob.type === 'application/octet-stream') {
        blob = new Blob([blob], { type: 'image/png' });
      }

      console.log('PNG blob created successfully, size:', blob.size, 'type:', blob.type);

      // Use the improved downloadBlob function for consistent mobile handling
      downloadBlob(blob, filename);
      
    } catch (err) {
      console.error('Error downloading image:', err);
      setDownloadStatus('Failed to download image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  }

  const handleDownload = async () => {
    try {
      setIsGenerating(true);
      setDownloadStatus('Generating high-quality visiting card PDF...');
      
      // Define all pages with their image sources (same as PNG download logic)
      const pages = [
        { 
          pageNumber: 0, 
          title: 'Visiting Card', 
          imageUrl: visitingCardImage,
          isDynamic: true 
        },
        { 
          pageNumber: 1, 
          title: 'Static Page 1', 
          imageUrl: imgUrl.visitingCard2,
          isDynamic: false 
        },
        { 
          pageNumber: 2, 
          title: 'Static Page 2', 
          imageUrl: imgUrl.visitingCard3,
          isDynamic: false 
        },
        { 
          pageNumber: 3, 
          title: 'Static Page 3', 
          imageUrl: imgUrl.visitingCard4,
          isDynamic: false 
        }
      ];
      
      // Ensure visiting card image is available
      if (!visitingCardImage || !visitingCardBlob) {
        setDownloadStatus('Loading visiting card image...');
        try {
          await fetchVisitingCard();
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (!visitingCardImage || !visitingCardBlob) {
            throw new Error('Visiting card image is still not available');
          }
          // Update the pages array with the loaded image
          pages[0].imageUrl = visitingCardImage;
        } catch (error) {
          console.error('Error fetching visiting card:', error);
          throw new Error('Failed to load visiting card image. Please try again.');
        }
      }
      
      // Get optimal dimensions from the first available image
      let optimalWidth = 0;
      let optimalHeight = 0;
      let sampleImage = null;
      
      // Try to get dimensions from visiting card first, then static images
      for (const page of pages) {
        if (page.imageUrl) {
          try {
            const response = await fetch(page.imageUrl);
            if (response.ok) {
              const blob = await response.blob();
              const base64 = await blobToBase64(blob);
              const img = new Image();
              
              await new Promise((resolve, reject) => {
                img.onload = () => {
                  optimalWidth = img.width;
                  optimalHeight = img.height;
                  sampleImage = img;
                  console.log(`Optimal dimensions set from ${page.title}: ${optimalWidth}x${optimalHeight}`);
                  resolve();
                };
                img.onerror = reject;
                img.src = base64;
              });
              break;
            }
          } catch (error) {
            console.log(`Failed to get dimensions from ${page.title}, trying next...`);
            continue;
          }
        }
      }
      
      if (optimalWidth === 0 || optimalHeight === 0) {
        throw new Error('Could not determine optimal dimensions for PDF');
      }
      
      let pdf = null;
      let firstImageLoaded = false;
      
      // Process each page with high-quality settings
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        console.log(`Processing page ${i + 1}: ${page.title}`);
        setDownloadStatus(`Processing page ${i + 1} of ${pages.length} with high quality...`);
        
        if (!page.imageUrl) {
          console.error(`No image URL available for page ${i + 1}`);
          continue;
        }
        
        // Validate image URL
        if (page.imageUrl.startsWith('blob:')) {
          try {
            const testResponse = await fetch(page.imageUrl);
            if (!testResponse.ok) {
              throw new Error('Blob URL is no longer valid');
            }
          } catch (error) {
            console.error('Blob URL validation failed:', error);
            if (page.isDynamic) {
              // For visiting card, try to refetch
              setDownloadStatus('Refreshing visiting card image...');
              await fetchVisitingCard(true);
              await new Promise(resolve => setTimeout(resolve, 1000));
              if (visitingCardImage) {
                page.imageUrl = visitingCardImage;
              } else {
                throw new Error('Failed to refresh visiting card image');
              }
            } else {
              throw new Error(`Image URL is no longer valid for ${page.title}`);
            }
          }
        }
        
        // Convert the image to blob and then to base64
        let imageBlob;
        
        if (page.isDynamic && page.pageNumber === 0) {
          // For visiting card, use the stored blob data
          imageBlob = visitingCardBlob;
          console.log('Using stored visiting card blob for PDF, size:', imageBlob.size);
        } else {
          // For static images, fetch from URL
          const imageResponse = await fetch(page.imageUrl);
          if (!imageResponse.ok) {
            throw new Error(`Failed to fetch image for ${page.title}: ${imageResponse.status} ${imageResponse.statusText}`);
          }
          imageBlob = await imageResponse.blob();
        }
        
        const base64 = await blobToBase64(imageBlob);
        
        // Load image and process with high quality
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            // Calculate high-quality dimensions while maintaining aspect ratio
            const maxWidth = 1200; // High resolution width
            const maxHeight = 1200; // High resolution height
            
            let finalWidth = img.width;
            let finalHeight = img.height;
            
            // Scale up if image is smaller than max dimensions, maintaining aspect ratio
            if (finalWidth < maxWidth && finalHeight < maxHeight) {
              const scaleX = maxWidth / finalWidth;
              const scaleY = maxHeight / finalHeight;
              const scale = Math.min(scaleX, scaleY, 2); // Limit scale to 2x to prevent excessive file size
              
              finalWidth = finalWidth * scale;
              finalHeight = finalHeight * scale;
            }
            
            // Create high-quality canvas for image processing
            const canvas = document.createElement('canvas');
            canvas.width = finalWidth;
            canvas.height = finalHeight;
            const ctx = canvas.getContext('2d');
            
            // Enable high-quality image rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.webkitImageSmoothingEnabled = true;
            ctx.mozImageSmoothingEnabled = true;
            ctx.msImageSmoothingEnabled = true;
            
            // Draw image with high quality
            ctx.drawImage(img, 0, 0, finalWidth, finalHeight);
            
            // Get high-quality base64 from canvas
            const highQualityBase64 = canvas.toDataURL('image/png', 1.0); // Maximum quality
            
            // Calculate PDF dimensions based on actual image size to eliminate white space
            // Convert pixels to mm (assuming 96 DPI: 1 inch = 25.4mm, 96 pixels = 25.4mm)
            const pixelsToMm = 25.4 / 96;
            const pdfWidth = finalWidth * pixelsToMm;
            const pdfHeight = finalHeight * pixelsToMm;
            
            // Create PDF with exact image dimensions (no white space)
            if (!firstImageLoaded) {
              pdf = new jsPDF({
                orientation: pdfWidth > pdfHeight ? "l" : "p", // Auto-detect orientation
                unit: "mm",
                format: [pdfWidth, pdfHeight], // Use exact image dimensions
                compress: false
              });
              firstImageLoaded = true;
            } else {
              // Add new page for subsequent images with their own dimensions
              pdf.addPage([pdfWidth, pdfHeight], pdfWidth > pdfHeight ? "l" : "p");
            }
            
            // Add the image to PDF at full size without any offset (fills entire page)
            pdf.addImage(highQualityBase64, "PNG", 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
            
            resolve();
          };
          img.onerror = () => {
            console.error(`Failed to load image for page ${i + 1}: ${page.title}`);
            reject(new Error(`Failed to load image for ${page.title}`));
          };
          img.src = base64;
        });
      }
      
      if (!pdf || !firstImageLoaded) {
        throw new Error('Failed to create PDF - no valid images found');
      }
      
      // Generate filename
      const baseFilename = buildFilename().replace('.png', '');
      const filename = `${baseFilename}.pdf`;
      
      // Generate PDF blob for download
      const pdfBlob = pdf.output("blob");
      
      setDownloadStatus('Downloading high-quality PDF...');
      
      // Use the improved downloadBlob function for consistent mobile handling
      downloadBlob(pdfBlob, filename);
      
      setDownloadStatus('High-quality PDF downloaded successfully!');
      setTimeout(() => setDownloadStatus(''), 3000);
      
    } catch (error) {
      console.error('Error downloading high-quality visiting card PDF:', error);
      setDownloadStatus('PDF download failed. Please try again.');
      setTimeout(() => setDownloadStatus(''), 3000);
    } finally {
      setIsGenerating(false);
    }
  };

  const nextPage = () => {
    const newPage = (currentPage + 1) % 4;
    setCurrentPage(newPage);
    
    // If going to page 0 (visiting card), check if the image is still valid
    if (newPage === 0 && visitingCardImage) {
      checkAndRefreshVisitingCard();
    }
  };

  const prevPage = () => {
    const newPage = (currentPage - 1 + 4) % 4;
    setCurrentPage(newPage);
    
    // If going to page 0 (visiting card), check if the image is still valid
    if (newPage === 0 && visitingCardImage) {
      checkAndRefreshVisitingCard();
    }
  };

  // Check if visiting card image is still valid and refresh if needed
  const checkAndRefreshVisitingCard = () => {
    if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
      // Test the blob URL to see if it's still valid
      const testImg = new Image();
      testImg.onload = () => {
        console.log('Visiting card blob URL is still valid');
      };
      testImg.onerror = () => {
        console.log('Visiting card blob URL is invalid, refreshing...');
        // Clear the invalid image and refetch
        if (visitingCardImage.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(visitingCardImage);
          } catch (e) {
            console.log('Error revoking blob URL:', e);
          }
        }
        setVisitingCardImage(null);
        fetchVisitingCard(true); // Force refetch
      };
      testImg.src = visitingCardImage;
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 0:
        return renderDynamicPage();
      case 1:
        return renderStaticPage1();
        case 2:
        return renderStaticPage2();
        case 3:
        return renderStaticPage3();
      default:
        return renderDynamicPage();
    }
  };

  const renderDynamicPage = () => (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      data-page="0"
      className="visiting-card-content"
    >
      {visitingCardLoading || isVisitingCardAutoGenerating ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={60} />
          <Typography sx={{ ml: 2 }}>
            {isVisitingCardAutoGenerating ? 'Generating visiting card automatically...' : 'Loading your visiting card...'}
          </Typography>
        </Box>
      ) : visitingCardError ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 4, gap: 2 }}>
          <Typography color="error" variant="h6">{visitingCardError}</Typography>
          {!isVisitingCardAutoGenerating && (
            <Button
              variant="contained"
              onClick={handleGenerateVisitingCard}
              disabled={isGenerating}
              sx={{
                bgcolor: '#4caf50',
                '&:hover': { bgcolor: '#45a049' }
              }}
            >
              {isGenerating ? 'Generating...' : 'Generate Visiting Card'}
            </Button>
          )}
          {isVisitingCardAutoGenerating && (
            <Typography color="info" variant="body2">
              Visiting card generation is in progress. Please wait...
            </Typography>
          )}
        </Box>
      ) : visitingCardImage ? (
        <>
        <Box sx={{ textAlign: 'center' }} >
          <img
            src={visitingCardImage}
            alt="I Am Peacekeeper Visiting Card"
            style={{
              maxWidth: '325px',
              height: '532px',
              borderRadius: '8px'
            }}
            onLoad={() => {
              console.log('Visiting card image loaded successfully');
            }}
            onError={(e) => {
              console.log('Visiting card image failed to load:', e);
              console.log('Image URL:', visitingCardImage);
              
              // Clear the invalid image immediately
              if (visitingCardImage && visitingCardImage.startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(visitingCardImage);
                } catch (revokeError) {
                  console.log('Error revoking blob URL:', revokeError);
                }
              }
              setVisitingCardImage(null);
              
              // Limit refetch attempts to prevent infinite loops
              if (refetchAttempts < 3) {
                setVisitingCardError('Image failed to load. Refetching...');
                setRefetchAttempts(prev => prev + 1);
                // Refetch the visiting card after clearing the state
                setTimeout(() => {
                  setVisitingCardError(null);
                  fetchVisitingCard(true); // Force refetch
                }, 1000);
              } else {
                setVisitingCardError('Failed to load visiting card after multiple attempts. Please try generating a new one.');
                setRefetchAttempts(0); // Reset for future attempts
              }
            }}
          />
          
        </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          {isVisitingCardAutoGenerating ? (
            <>
              <CircularProgress size={40} />
              <Typography sx={{ ml: 2 }}>Generating visiting card automatically...</Typography>
            </>
          ) : (
            <>
              <Typography color="text.secondary">No visiting card available</Typography>
              <Button
                variant="contained"
                onClick={handleGenerateVisitingCard}
                disabled={isGenerating}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#45a049' }
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Visiting Card'}
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );

  const renderStaticPage3 = () => (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      }}
      data-page="3"
      className="visiting-card-content"
    >
      <img 
        src={imgUrl.visitingCard4} 
        alt="Visiting Card Page 4"
        style={{
              maxWidth: '325px',
              height: '532px',
              borderRadius: '8px'
            }}
      />
    </Box>
  );

  const renderStaticPage2 = () => (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      }}
      data-page="2"
      className="visiting-card-content"
    >
      <img 
        src={imgUrl.visitingCard3} 
        alt="Visiting Card Page 3"
        style={{
              maxWidth: '325px',
              height: '532px',
              borderRadius: '8px'
            }}
      />
    </Box>
  );

  const renderStaticPage1 = () => (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      }}
      data-page="1"
      className="visiting-card-content"
    >
      <img 
        src={imgUrl.visitingCard2} 
        alt="Visiting Card Page 2"
        style={{
              maxWidth: '325px',
              height: '532px',
              borderRadius: '8px'
            }}
      />
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>

      {/* Page Indicator */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 3,
        gap: 1,
        alignItems: 'center'
      }}>
        {/* Previous Button - Left side */}
        <Button
          variant="outlined"
          startIcon={<NavigateBeforeIcon />}
          onClick={prevPage}
          disabled={isGenerating}
          size="small"
          sx={{
            minWidth: 'auto',
            px: 2,
            py: 0.5
          }}
        >
          
        </Button>
        
        {/* Page Dots */}
        {[0, 1, 2, 3].map((page) => (
          <Box
            key={page}
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: currentPage === page ? '#3A8139' : '#e0e0e0',
              cursor: 'pointer'
            }}
            onClick={() => {
              setCurrentPage(page);
              // If clicking on page 0 (visiting card), check if the image is still valid
              if (page === 0 && visitingCardImage) {
                checkAndRefreshVisitingCard();
              }
            }}
          />
        ))}
        
        {/* Next Button - Right side */}
        <Button
          variant="outlined"
          endIcon={<NavigateNextIcon />}
          onClick={nextPage}
          disabled={isGenerating}
          size="small"
          sx={{
            minWidth: 'auto',
            px: 2,
            py: 0.5
          }}
        >
          
        </Button>
      </Box>

      {/* Status Message */}
      {downloadStatus && (
        <Box sx={{ 
          textAlign: 'center', 
          mb: 2,
          p: 1,
          bgcolor: downloadStatus.includes('successfully') ? 'success.light' : 'info.light',
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            {downloadStatus}
          </Typography>
        </Box>
      )}

      {/* Visiting Card Content */}
      <Box 
        className="visiting-card-content"
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          width: '100%',
          minHeight: '600px'
        }}
      >
        {currentPage === 0 ? (
          <Box ref={visitingCardRef} sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {renderDynamicPage()}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {renderPage()}
          </Box>
        )}
      </Box>

      {/* Page Info */}
      <Box sx={{ 
        textAlign: 'center', 
        mt: 3,
        p: 2,
        bgcolor: 'grey.50',
        borderRadius: 1
      }}>
        <Typography variant="body2" color="text.secondary">
          Page {currentPage + 1} of 4
          {currentPage === 0}
          {currentPage === 1} 
          {currentPage === 2}
          {currentPage === 3}
        </Typography>
      </Box>

       {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        
         <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPNG}
            disabled={isGenerating || visitingCardLoading || isVisitingCardAutoGenerating}
            sx={{
              borderColor: '#FF6B35',
              color: '#FF6B35',
              '&:hover': { 
                borderColor: '#E55A2B',
                backgroundColor: 'rgba(255, 107, 53, 0.04)'
              }
            }}
          >
            {isGenerating ? 'Generating...' : 'Download PNG'}
          </Button>
          
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownload}
            disabled={isGenerating || !visitingCardImage || visitingCardLoading || isVisitingCardAutoGenerating}
            sx={{
              bgcolor: '#3A8139',
              color: 'white',
              '&:hover': { bgcolor: '#2d652c' }
            }}
          >
            {isGenerating ? 'Generating...' : 'Download All Pages as PDF'}
          </Button>
          
        </Box>
      </Box>
    </Box>
  );
};

export default VisitingCard;